// The admin audit log (D56): every staff action and view, newest first, with who, what, which
// organization and target, before and after, and the reason. Append-only in Postgres; reading
// it is logged too.
import { PageHead, Section } from '@apsio/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type AdminAuditEntry, auditPagesQuery } from '../api/queries'
import { DataTable, IdText } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/
/** The token automatic views carry as their reason (organization.viewed, erasures.listed…). */
const READ_REASON = 'read'
/** The reasons the API writes itself, in English: said in the console's language. */
const SYSTEM_REASON: Record<string, MessageId> = {
  'staff preference': 'audit.reason.preference',
  expired: 'audit.reason.expired',
  reconcile: 'audit.reason.reconcile',
  "Apsio's funnel, error groups and logs from PostHog": 'audit.reason.posthog',
}

/** One changed key: what it was and what it became; a key only one side has shows that side. */
type Changed = { key: string; before?: unknown; after?: unknown }

/** The keys whose value differs between before and after, in before's order, then after's. */
export function changedKeys(entry: Pick<AdminAuditEntry, 'before' | 'after'>): Changed[] {
  const before = entry.before ?? {}
  const after = entry.after ?? {}
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  const out: Changed[] = []
  for (const key of keys) {
    const b = Object.hasOwn(before, key)
    const a = Object.hasOwn(after, key)
    if (b && a && JSON.stringify(before[key]) === JSON.stringify(after[key])) continue
    out.push({ key, ...(b ? { before: before[key] } : {}), ...(a ? { after: after[key] } : {}) })
  }
  return out
}

/** A value as the change column shows it: ids shortened, times formatted, the rest as data. */
function Value({ value }: { value: unknown }) {
  const { t, number, dateTime } = useI18n()
  if (value === null || value === undefined || value === '') return <>{t('common.none')}</>
  if (typeof value === 'number') return <>{number(value)}</>
  if (typeof value === 'boolean') return <>{t(value ? 'audit.yes' : 'audit.no')}</>
  // A list of plain values reads as one, comma separated; an empty list is nothing.
  if (Array.isArray(value) && value.every((v) => typeof v !== 'object' || v === null))
    return (
      <>
        {value.length === 0
          ? t('common.none')
          : value
              .map((v) =>
                typeof v === 'number'
                  ? number(v)
                  : typeof v === 'boolean'
                    ? t(v ? 'audit.yes' : 'audit.no')
                    : v === null
                      ? t('common.none')
                      : String(v),
              )
              .join(', ')}
      </>
    )
  // Only an object or a list of objects is left as data.
  if (typeof value !== 'string') return <>{JSON.stringify(value)}</>
  if (UUID.test(value)) return <IdText id={value} copy={false} />
  if (ISO_TIME.test(value) && !Number.isNaN(Date.parse(value))) return <>{dateTime(value)}</>
  return <>{value}</>
}

/** before → after, one changed key per line, mono; customer data and codes, never translated. */
function Change({ entry }: { entry: AdminAuditEntry }) {
  return changedKeys(entry).map((c) => (
    <div key={c.key}>
      <span className="dim">{c.key}</span> {'before' in c ? <Value value={c.before} /> : null}
      {'before' in c && 'after' in c ? ' → ' : null}
      {'after' in c ? <Value value={c.after} /> : null}
    </div>
  ))
}

/** What an entry acted on: its kind, then the id (an organization's is the link), or a plain word. */
function Target({ entry: e }: { entry: AdminAuditEntry }) {
  const id = UUID.test(e.target_id) ? (
    e.org_id && e.org_id === e.target_id ? (
      <Link to="/orgs/$orgId" params={{ orgId: e.org_id }}>
        <IdText id={e.target_id} copy={false} />
      </Link>
    ) : (
      <IdText id={e.target_id} />
    )
  ) : (
    e.target_id
  )
  return (
    <>
      {e.target_type} {id}
    </>
  )
}

export function AuditTable({ entries }: { entries: AdminAuditEntry[] }) {
  const { t, dateTime } = useI18n()
  const reasonOf = (reason: string) =>
    reason === READ_REASON
      ? t('common.none')
      : Object.hasOwn(SYSTEM_REASON, reason)
        ? t(SYSTEM_REASON[reason] as MessageId)
        : reason
  if (entries.length === 0) return <Empty title={t('audit.empty')} />
  return (
    // When, what and on what come first, so a phone shows them before the scroll (APS-412 G1).
    <DataTable label={t('audit.table')} minWidth={880} className="audit">
      <thead>
        <tr>
          <th scope="col">{t('audit.col.when')}</th>
          <th scope="col">{t('audit.col.action')}</th>
          <th scope="col">{t('audit.col.target')}</th>
          <th scope="col">{t('audit.col.who')}</th>
          <th scope="col">{t('audit.col.change')}</th>
          <th scope="col">{t('audit.col.reason')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{dateTime(e.created_at)}</td>
            <td className="cell-mono" translate="no">
              {e.action}
            </td>
            <td className="cell-mono" translate="no">
              <Target entry={e} />
            </td>
            <td className="cell-mono" translate="no">
              {e.staff_user_id ? <IdText id={e.staff_user_id} /> : t('audit.system')}
            </td>
            <td className="cell-mono change" translate="no">
              <Change entry={e} />
            </td>
            <td className="reason" translate="no">
              {reasonOf(e.reason)}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}

export function AuditPage() {
  const { t } = useI18n()
  const audit = useInfiniteQuery(auditPagesQuery())
  const entries = audit.data?.pages.flat() ?? []
  return (
    <main id="main" className="pg">
      <PageHead title={t('audit.title')} meta={t('audit.lede')} />
      <Section
        title={audit.data ? t('audit.recentCount', { count: entries.length }) : t('audit.recent')}
      >
        {audit.isPending ? (
          <Loading />
        ) : audit.isError ? (
          <ErrorState error={audit.error} retry={() => audit.refetch()} />
        ) : (
          <>
            <AuditTable entries={entries} />
            {audit.hasNextPage ? (
              <div className="g1-more">
                <button
                  type="button"
                  className="btn sm"
                  disabled={audit.isFetchingNextPage}
                  onClick={() => audit.fetchNextPage()}
                >
                  {t('audit.more')}
                </button>
              </div>
            ) : null}
          </>
        )}
      </Section>
    </main>
  )
}
